import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { useTranslation } from "../../context/TranslationContext";
import { useCurrency } from "../../context/CurrencyContext";
import { IMAGE_URL } from "../../network/apiClient";
import apiCategory from "../../apiProvider/categoryApi";
import apiCart from "../../apiProvider/addToCartApi";
import ApiProvider from "../../apiProvider/api";
import { toast } from 'react-toastify';
import './home.css';

const KidsDetailPage = () => {
    const { id } = useParams(); // Using ID from URL same as other pages
    const navigate = useNavigate();
    const { translateSync } = useTranslation();
    const { getCurrencySymbol, getProductPrice, getProductMrp } = useCurrency();
    const [product, setProduct] = useState(null);
    const [currentImage, setCurrentImage] = useState(null);
    const [isAddingMode, setIsAddingMode] = useState(false);
    const [activeTab, setActiveTab] = useState('description');
    const [selectedAttributes, setSelectedAttributes] = useState({}); // Stores { "attributeName": valueObject }
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [loadingRelated, setLoadingRelated] = useState(true);
    const [quantity, setQuantity] = useState(1);

    useEffect(() => {
        if (isPreviewOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isPreviewOpen]);

    const colorSwatchMap = {
        black: "#1f1f1f",
        blue: "#1e3a8a",
        brown: "#6b4f2f",
        gold: "#d4af37",
        gray: "#6b7280",
        grey: "#6b7280",
        green: "#166534",
        maroon: "#800000",
        navy: "#1f2a44",
        orange: "#c2410c",
        pink: "#be185d",
        purple: "#5b21b6",
        red: "#b91c1c",
        silver: "#9ca3af",
        white: "#f9fafb",
        yellow: "#ca8a04"
    };

    const getColorSwatch = (value) => {
        const normalized = String(value || "").trim().toLowerCase();
        if (!normalized) return "#ddd";
        if (/^#|^rgb|^hsl/i.test(normalized)) return normalized;
        return colorSwatchMap[normalized] || "#ddd";
    };


    const cleanText = (value) => (value || "").replace(/\r?\n/g, " ").replace(/\s+/g, " ").trim();
    const stripHtml = (html) => (html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

    const buildImageUrl = (image) => {
        if (!image || !image.docPath || !image.docName) return null;
        return `${IMAGE_URL}${image.docPath}/${image.docName}`;
    };

    const autoSelectFirstPath = (currentAttributes, nextAttr) => {
        let updatedSelection = { ...currentAttributes };
        let walker = nextAttr;
        while (walker && walker.value && walker.value.length > 0) {
            const firstOption = walker.value[0];
            updatedSelection[walker.name] = firstOption;
            walker = firstOption.attributes?.[0];
        }
        return updatedSelection;
    };

    const getPrimaryAttribute = () => {
        if (!product?.customerAttributeDetails || product.customerAttributeDetails.length === 0) return null;
        return product.customerAttributeDetails[0];
    };

    const getSizeOptions = () => {
        const attr = getPrimaryAttribute();
        return attr?.value || [];
    };

    const fetchRelatedProducts = async (subCatId) => {
        try {
            setLoadingRelated(true);
            const prodResult = await apiCategory.getCategoryDet(null, 20, 0, subCatId);
            if (prodResult?.status && prodResult?.response?.data) {
                // Filter out current product and take 4
                const filtered = prodResult.response.data
                    .filter(p => p._id !== id)
                    .sort(() => 0.5 - Math.random())
                    .slice(0, 4);
                setRelatedProducts(filtered);
            }
        } catch (error) {
            console.error("Error fetching related products:", error);
        } finally {
            setLoadingRelated(false);
        }
    };

    const getProDetails = async () => {
        try {
            const result = await ApiProvider.getProductDetails(id);
            if (result.status && result.response) {
                setProduct(result.response.data.data)
            }
        } catch (error) {
            console.error("Error fetching product details:", error);
        }
    };

    useEffect(() => {
        getProDetails();
    }, [id]);

    useEffect(() => {
        const triggerRelatedProducts = async () => {
            let subCatId = product?.subCategoryId

            console.log("Kids Detail - Final SubCategoryID:", subCatId, "for product:", product?.productName);
            if (subCatId) {
                fetchRelatedProducts(subCatId);
            }
        };

        if (product) {
            triggerRelatedProducts();
        }
    }, [product, id]);

    useEffect(() => {
        if (!product) return;

        // Reset quantity
        setQuantity(1);

        // 1. Auto-select the first available path
        const initialSelections = autoSelectFirstPath({}, product.customerAttributeDetails?.[0]);
        setSelectedAttributes(initialSelections);

        // 2. Determine initial image from the matched row
        const initialRow = getSelectedRow(initialSelections);
        const initialImageEntry = initialRow?.images?.[0] || product.customerAttribute?.rowData?.[0]?.images?.[0] || product.productImage?.[0] || product.additionalImage?.[0] || null;

        const imageUrl = buildImageUrl(initialImageEntry);
        setCurrentImage(imageUrl);

        window.scrollTo(0, 0);
    }, [product]);

    const handleAddToCart = async () => {
        setIsAddingMode(true);
        try {
            const primaryAttr = getPrimaryAttribute();
            if (!primaryAttr) {
                toast.error("Product configuration error: No attributes found.");
                return;
            }

            // Validate all nested attributes in the current selected path
            let currentAttr = primaryAttr;
            const attributesPayload = {};

            while (currentAttr) {
                const selectedVal = selectedAttributes[currentAttr.name];
                if (!selectedVal) {
                    toast.warn(`Please select a ${currentAttr.name} before adding to cart!`);
                    setIsAddingMode(false);
                    return;
                }
                attributesPayload[currentAttr.name] = selectedVal._id || selectedVal.id;
                currentAttr = selectedVal.attributes?.[0];
            }

            const finalRow = getSelectedRow();
            const currentPrice = Number(finalRow?.price || product.price || 0);
            const currentMrp = Number(finalRow?.customermrp || product.mrpPrice || 0);

            const token = localStorage.getItem('userToken');
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            let payload = {
                products: [
                    {
                        productId: product._id,
                        quantity: quantity,
                        price: currentPrice,
                        mrpPrice: currentMrp,
                        attributes: attributesPayload
                    }
                ],
                total: currentPrice * quantity,
                subtotal: currentPrice * quantity
            };

            if (token && user._id) {
                payload.type = 'user';
                payload.userId = user._id;
                payload.userType = 'user';
            } else {
                payload.type = 'guest';
                let guestId = localStorage.getItem('guestUserId');
                if (!guestId) {
                    guestId = 'guest_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
                    localStorage.setItem('guestUserId', guestId);
                }
                payload.guestUserId = guestId;
                payload.userType = 'guest';
            }

            const result = await apiCart.addToCart(payload);

            if (result && result.status && result.response) {
                toast.success('Item added to shopping bag');
            } else {
                toast.warn(result?.response?.message || 'Failed to add to cart');
            }

        } catch (error) {
            console.error("Add to cart error", error);
            toast.error("An error occurred");
        } finally {
            setIsAddingMode(false);
        }
    };

    const getSelectedRow = (customAttributes = selectedAttributes) => {
        if (!product?.customerAttribute?.rowData) return null;
        const rowData = product.customerAttribute.rowData;

        // Find row that matches all currently selected attribute values
        return rowData.find(row => {
            return Object.entries(customAttributes).every(([attrName, selectedVal]) => {
                const selectedId = selectedVal._id || selectedVal.id;
                const actualKey = Object.keys(row).find(k => {
                    const kLower = k.toLowerCase();
                    const attrLower = attrName.toLowerCase();
                    if (kLower === attrLower) return true;
                    if ((kLower === 'color' || kLower === 'colour') && (attrLower === 'color' || attrLower === 'colour')) return true;
                    return false;
                });
                if (!actualKey) return false;
                return String(row[actualKey]) === String(selectedId);
            });
        });
    };

    const getActiveImages = () => {
        if (!product) return [];
        const selectedRow = getSelectedRow();

        let sources = [];
        if (selectedRow?.images && selectedRow.images.length > 0) {
            sources = selectedRow.images;
        } else {
            // Fallback to product images only if no variant images exist
            sources = [
                ...(product.productImage || []),
                ...(product.additionalImage || [])
            ];
        }

        return sources.reduce((acc, img) => {
            const url = buildImageUrl(img);
            if (!url) return acc;
            if (acc.some((item) => item.url === url)) return acc;
            acc.push({ id: img._id || img.docName || url, url });
            return acc;
        }, []);
    };

    if (!product) return <div className="text-center py-5">Loading...</div>;


    const imageItems = getActiveImages();

    const mainImageUrl = currentImage || imageItems[0]?.url || "/img/kids/default.png";

    const selectedRow = getSelectedRow();
    const isInStock = selectedRow ? Number(selectedRow.stock) > 0 : false;
    const priceValue = getProductPrice(product, selectedRow);
    const mrpValue = getProductMrp(product, selectedRow);
    const displayPrice = priceValue > 0 ? priceValue : null;
    const displayMrp = mrpValue > 0 ? mrpValue : null;
    const discount = displayMrp && displayPrice && displayMrp > displayPrice
        ? Math.round(((displayMrp - displayPrice) / displayMrp) * 100)
        : 0;


    const rawShortDesc = cleanText(product.shortDescription);
    const shortDescription = rawShortDesc ? rawShortDesc.charAt(0).toUpperCase() + rawShortDesc.slice(1).toLowerCase() : "";
    const metaDescription = cleanText(product.metaDesc) || stripHtml(product.description) || shortDescription;

    const specificationRows = [
        { label: "Product Code", value: product.productCode || product._id || "-" }
    ];

    if (Array.isArray(product.specifications)) {
        product.specifications.forEach((spec) => {
            if (!spec?.key) return;
            specificationRows.push({ label: spec.key, value: spec.value || "-" });
        });
    }
    if (product.categoryName) specificationRows.push({ label: "Category", value: product.categoryName });
    if (product.subCategoryName) specificationRows.push({ label: "Sub Category", value: product.subCategoryName });
    if (product.brandName) specificationRows.push({ label: "Brand", value: product.brandName });
    if (typeof product.refundable === "boolean") specificationRows.push({ label: "Return", value: product.refundable ? "Returnable" : "No Return" });


    return (
        <>
            <Helmet>
                <title>{product.productName} | Colours Boutique</title>
                <meta name="description" content={metaDescription} />
            </Helmet>

            <div className="boutique-detail-wrapper">
                <div className="container">

                    {/* Breadcrumb */}
                    <div className="row mb-4">
                        <div className="col-12">
                            <nav aria-label="breadcrumb" className="boutique-breadcrumb-nav">
                                <ol className="boutique-breadcrumb-ol">
                                    <li className="breadcrumb-item"><span onClick={() => navigate('/')}>Home</span></li>
                                    <li className="breadcrumb-item"><span onClick={() => navigate('/kids')}>Kids</span></li>
                                    <li className="breadcrumb-item active" aria-current="page">{product.productName}</li>
                                </ol>
                            </nav>
                        </div>
                    </div>

                    <div className="row g-5">
                        {/* LEFT COLUMN: IMAGES */}
                        <div className="col-lg-6">
                            <div className="product-image-container sticky-top">
                                <div className="main-image-frame mb-3">
                                    <img
                                        src={mainImageUrl}
                                        alt={product.productName}
                                        className="img-fluid w-100 h-100 main-detail-img"
                                        onClick={() => setIsPreviewOpen(true)}
                                    />
                                </div>
                                {imageItems.length > 1 && (
                                    <div className="d-flex gap-2 justify-content-center flex-wrap">
                                        {imageItems.map((img, i) => (
                                            <div
                                                key={i}
                                                className="product-thumbnail-item"
                                                style={{
                                                    border: currentImage === img.url ? "2px solid #222" : "1px solid #ddd",
                                                    padding: "2px",
                                                    cursor: 'pointer',
                                                    opacity: currentImage === img.url ? 1 : 0.6
                                                }}
                                                onClick={() => setCurrentImage(img.url)}
                                            >
                                                <img src={img.url} className="w-100 h-100" style={{ objectFit: "cover" }} alt="" />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* RIGHT COLUMN: DETAILS */}
                        <div className="col-lg-6">
                            <div className="product-info ps-lg-4 text-start">
                                <h1 className="product-title " style={{ fontFamily: "'Playfair Display', serif", fontSize: "2.5rem", color: "#222", marginBottom: "10px" }}>
                                    {product.productName}
                                </h1>
                                <p className="text-muted small mb-3" style={{ letterSpacing: "2px" }}>
                                    {shortDescription || product.tagAndLabel || product.subCategoryName || ""}
                                </p>

                                <div className="d-flex align-items-center mb-4">
                                    <div className="text-warning small me-2">
                                        <i className="fa fa-star"></i><i className="fa fa-star"></i><i className="fa fa-star"></i><i className="fa fa-star"></i><i className="fa fa-star"></i>
                                    </div>
                                    <span className="text-muted small">(Review)</span>
                                </div>

                                <div className="price-area mb-4 pb-4 border-bottom">
                                    {priceValue === null ? (
                                        <div className="d-flex align-items-baseline gap-3">
                                            <span className="current-price" style={{ fontSize: "1.5rem", fontWeight: "600", color: "#B12704" }}>
                                                Currency not available
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="d-flex align-items-baseline gap-3">
                                            <span className="current-price" style={{ fontSize: "2rem", fontWeight: "600", color: "#B12704" }}>
                                                {getCurrencySymbol()} {displayPrice ? displayPrice.toLocaleString() : "--"}
                                            </span>
                                            {/* {displayMrp && (
                                                <span className="original-price text-muted text-decoration-line-through" style={{ fontSize: "1.2rem" }}>
                                                    {getCurrencySymbol()} {displayMrp.toLocaleString()}
                                                </span>
                                            )}
                                            {discount > 0 && (
                                                <span className="discount-badge px-2 py-1 small" style={{ backgroundColor: "#222", color: "#fff" }}>
                                                    {discount}% OFF
                                                </span>
                                            )} */}
                                        </div>
                                    )}
                                    <div className="text-success small mt-1 fw-bold">Inclusive of all taxes</div>
                                </div>

                                {/* Dynamic Nested Attributes */}
                                {(() => {
                                    const renderedSections = [];
                                    let currentAttr = product.customerAttributeDetails?.[0];
                                    let depth = 0;

                                    while (currentAttr && depth < 5) {
                                        const attrName = currentAttr.name;
                                        const options = currentAttr.value || [];
                                        const selectedVal = selectedAttributes[attrName];
                                        const isColorAttr = attrName.toLowerCase() === "color";

                                        if (!isColorAttr) {
                                            renderedSections.push(
                                                <div key={attrName} className="d-flex align-items-center mb-3">
                                                    <div className="fw-bold text-muted me-3" style={{ minWidth: "70px", fontSize: "1.1rem", textTransform: "capitalize" }}>
                                                        {attrName}:
                                                    </div>
                                                    <div className="d-flex gap-3 flex-wrap">
                                                        {options.map((option, idx) => {
                                                            const valLabel = option.value || option.name || option;
                                                            const isSelected = selectedVal && (selectedVal._id === option._id || selectedVal === option);
                                                            const attributeImg = option.attributeImages?.[0] ? buildImageUrl(option.attributeImages[0]) : null;

                                                            return (
                                                                <button
                                                                    key={idx}
                                                                    onClick={() => {
                                                                        const nextSelections = (() => {
                                                                            const cleanedNext = {};
                                                                            let walker = product.customerAttributeDetails?.[0];
                                                                            while (walker) {
                                                                                const wName = walker.name;
                                                                                if (wName === attrName) {
                                                                                    cleanedNext[wName] = option;
                                                                                    return autoSelectFirstPath(cleanedNext, option.attributes?.[0]);
                                                                                }
                                                                                if (selectedAttributes[wName]) cleanedNext[wName] = selectedAttributes[wName];
                                                                                walker = selectedAttributes[wName]?.attributes?.[0];
                                                                            }
                                                                            return cleanedNext;
                                                                        })();

                                                                        setSelectedAttributes(nextSelections);

                                                                        // Determine image for the new selection
                                                                        const nextRow = getSelectedRow(nextSelections);
                                                                        if (option.images && option.images.length > 0) {
                                                                            setCurrentImage(buildImageUrl(option.images[0]));
                                                                        } else if (nextRow?.images && nextRow.images.length > 0) {
                                                                            setCurrentImage(buildImageUrl(nextRow.images[0]));
                                                                        } else {
                                                                            const fallback = product.customerAttribute?.rowData?.[0]?.images?.[0] || product.productImage?.[0] || product.additionalImage?.[0];
                                                                            setCurrentImage(buildImageUrl(fallback));
                                                                        }
                                                                    }}
                                                                    className={`btn position-relative d-flex align-items-center justify-content-center ${attributeImg ? "p-0 rounded-circle" : "py-1 px-3 rounded-3"}`}
                                                                    style={{
                                                                        minWidth: attributeImg ? "44px" : "50px",
                                                                        height: attributeImg ? "44px" : "auto",
                                                                        backgroundColor: attributeImg ? "transparent" : (isSelected ? "#222" : "#fff"),
                                                                        color: isSelected ? "#fff" : "#222",
                                                                        border: attributeImg
                                                                            ? "none"
                                                                            : (isSelected ? "1px solid #222" : "1px solid #7a6446"),
                                                                        outline: isSelected && attributeImg ? "2px solid #222" : "none",
                                                                        outlineOffset: "3px",
                                                                        transition: "all 0.2s",
                                                                        fontSize: "1rem",
                                                                        fontWeight: "500",
                                                                        textTransform: "uppercase",
                                                                        padding: attributeImg ? "3px" : "8px 16px",
                                                                        overflow: "hidden",
                                                                        borderRadius: attributeImg ? "50%" : "8px"
                                                                    }}
                                                                    title={valLabel}
                                                                >
                                                                    {attributeImg ? (
                                                                        <img src={attributeImg} alt={valLabel} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
                                                                    ) : (
                                                                        valLabel
                                                                    )}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        }

                                        if (selectedVal && selectedVal.attributes && selectedVal.attributes.length > 0) {
                                            currentAttr = selectedVal.attributes[0];
                                            depth++;
                                        } else {
                                            currentAttr = null;
                                        }
                                    }
                                    return renderedSections;
                                })()}

                                {/* Quantity Selector */}
                                <div className="d-flex align-items-center mb-4 gap-3">
                                    <span className="text-muted text-uppercase small fw-bold" style={{ letterSpacing: "1px" }}>Quantity:</span>
                                    <div className="d-flex align-items-center border border-dark rounded-3" style={{ width: "120px", height: "45px", backgroundColor: "#fff" }}>
                                        <button 
                                            type="button" 
                                            className="btn btn-link text-dark px-3 py-1 text-decoration-none" 
                                            onClick={() => setQuantity(q => Math.max(1, q - 1))}
                                            style={{ fontSize: "1.2rem", fontWeight: "bold" }}
                                        >
                                            -
                                        </button>
                                        <span className="flex-grow-1 text-center fw-bold" style={{ fontSize: "1.1rem" }}>{quantity}</span>
                                        <button 
                                            type="button" 
                                            className="btn btn-link text-dark px-3 py-1 text-decoration-none" 
                                            onClick={() => setQuantity(q => q + 1)}
                                            style={{ fontSize: "1.2rem", fontWeight: "bold" }}
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="action-buttons mb-5">
                                    <div className="row">
                                        <div className="col-12">
                                            <button
                                                className="btn w-100 py-3 text-uppercase fw-bold"
                                                onClick={handleAddToCart}
                                                disabled={isAddingMode || !isInStock || priceValue === null}
                                                style={{
                                                    backgroundColor: (isInStock && priceValue !== null) ? "#222" : "#ccc",
                                                    color: "#fff",
                                                    border: (isInStock && priceValue !== null) ? "1px solid #222" : "1px solid #ccc",
                                                    letterSpacing: "1px",
                                                    transition: "0.3s",
                                                    cursor: (isInStock && priceValue !== null) ? "pointer" : "not-allowed"
                                                }}
                                                onMouseOver={(e) => {
                                                    if (isInStock && priceValue !== null) e.target.style.backgroundColor = '#444'
                                                }}
                                                onMouseOut={(e) => {
                                                    if (isInStock && priceValue !== null) e.target.style.backgroundColor = '#222'
                                                }}
                                            >
                                                {isAddingMode ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Adding...
                                                    </>
                                                ) : (
                                                    priceValue === null ? 'Unavailable' : (isInStock ? 'Add to Cart' : 'Out of Stock')
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Product Services */}
                                <div className="services-row d-flex justify-content-between text-center mb-5 pb-4 border-bottom">
                                    <div>
                                        <div className="mb-2"><i className="fa fa-truck fa-lg text-secondary"></i></div>
                                        <span className="small text-uppercase">Free Shipping</span>
                                    </div>
                                    <div>
                                        <div className="mb-2"><i className="fa fa-certificate fa-lg text-secondary"></i></div>
                                        <span className="small text-uppercase">Quality Assured</span>
                                    </div>
                                    <div>
                                        <div className="mb-2"><i className="fa fa-lock fa-lg text-secondary"></i></div>
                                        <span className="small text-uppercase">Secure Pay</span>
                                    </div>
                                    <div>
                                        <div className="mb-2"><i className="fa fa-undo fa-lg text-secondary"></i></div>
                                        <span className="small text-uppercase">{product.refundable ? "Returnable" : "No Return"}</span>
                                    </div>
                                </div>

                                {/* Details / Specifications Tabs */}
                                <div className="product-tabs">
                                    <ul className="nav nav-tabs border-0 mb-3 gap-4">
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link border-0 bg-transparent p-0 pb-2 text-uppercase fw-bold ${activeTab === 'description' ? 'active-tab' : 'text-muted'}`}
                                                style={{ borderBottom: activeTab === 'description' ? "2px solid #222" : "none", borderRadius: 0 }}
                                                onClick={() => setActiveTab('description')}
                                            >
                                                Description
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link border-0 bg-transparent p-0 pb-2 text-uppercase fw-bold ${activeTab === 'specs' ? 'active-tab' : 'text-muted'}`}
                                                style={{ borderBottom: activeTab === 'specs' ? "2px solid #222" : "none", borderRadius: 0 }}
                                                onClick={() => setActiveTab('specs')}
                                            >
                                                Specifications
                                            </button>
                                        </li>
                                    </ul>

                                    <div className="tab-content text-muted" style={{ fontSize: "0.95rem", lineHeight: "1.8" }}>
                                        {activeTab === 'description' && (
                                            <div className="fade show">
                                                {product.description ? (
                                                    <div
                                                        className="mb-3"
                                                        dangerouslySetInnerHTML={{ __html: product.description }}
                                                    />
                                                ) : (
                                                    <p className="mb-3">{shortDescription}</p>
                                                )}
                                                <p>Designed for comfort and style, this piece ensures your little one looks their best for any special occasion. Made with soft, skin-friendly fabrics.</p>
                                            </div>
                                        )}
                                        {activeTab === 'specs' && (
                                            <div className="fade show">
                                                <table className="table table-borderless table-sm">
                                                    <tbody>
                                                        {specificationRows.map((row, idx) => (
                                                            <tr key={`${row.label}-${idx}`}>
                                                                <td className="fw-bold text-dark" width="40%">{row.label}</td>
                                                                <td>{row.value}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                    {/* RELATED Kids SECTION */}
                    <div className="row mt-5 pt-5 border-top">
                        <div className="col-12 mb-4 text-center">
                            <h3 style={{ fontFamily: "'Playfair Display', serif", color: "#222" }}>You May Also Like</h3>
                        </div>
                        {relatedProducts.length > 0 ? (
                            <div className="row g-4 w-100 m-0">
                                {relatedProducts.map((related, idx) => {
                                    const relImgData = related.customerAttribute?.rowData?.[0]?.images?.[0] || related.productImage?.[0] || related.additionalImage?.[0];
                                    const relImgSrc = relImgData ? `${IMAGE_URL}${relImgData.docPath}/${relImgData.docName}` : '/img/products/default.png';

                                    return (
                                        <div className="col-6 col-md-3 mb-4" key={idx} onClick={() => navigate(`/kids/${related._id}`)} style={{ cursor: 'pointer' }}>
                                            <div className="collection-item">
                                                <div className="collection-image-wrapper">
                                                    <img
                                                        src={relImgSrc}
                                                        alt={related.productName}
                                                        className="collection-image"
                                                        onError={(e) => { e.target.src = '/img/products/default.png'; }}
                                                    />
                                                </div>

                                                {(() => {
                                                    const firstRow = related.customerAttribute?.rowData?.[0];
                                                    const priceValue = getProductPrice(related, firstRow);
                                                    const displayPrice = priceValue > 0 ? priceValue : null;

                                                    return (
                                                        <div className="text-start mt-3">
                                                            <h4 style={{
                                                                fontSize: '1.15rem',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                marginBottom: '8px',
                                                                lineHeight: '1.4',
                                                                textTransform: 'capitalize'
                                                            }}>
                                                                {related.productName}
                                                            </h4>
                                                            <div className="d-flex justify-content-between align-items-center mt-2">
                                                                <div className="d-flex flex-wrap align-items-baseline gap-2">
                                                                    {priceValue === null ? (
                                                                        <span className="text-muted" style={{ fontSize: '0.9rem', fontStyle: 'italic' }}>
                                                                            Currency not available
                                                                        </span>
                                                                    ) : (
                                                                        <>
                                                                            <span className="fw-bold" style={{ fontSize: '1.1rem', color: '#B12704' }}>
                                                                                {getCurrencySymbol()}{displayPrice ? displayPrice.toLocaleString() : "0.00"}
                                                                            </span>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : !loadingRelated ? (
                            <div className="col-12 text-center py-4">
                                <p className="text-muted">No related products found in this category.</p>
                            </div>
                        ) : (
                            <div className="col-12 text-center py-4">
                                <div className="spinner-border spinner-border-sm text-secondary" role="status"></div>
                            </div>
                        )}
                    </div>

                </div>
            </div>
            {/* Full Screen Image Preview Modal */}
            {isPreviewOpen && (
                <div
                    className="fixed-top w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{
                        backgroundColor: "#ffffff",
                        zIndex: 9999,
                        cursor: "zoom-out",
                        animation: "fadeIn 0.3s ease-in-out"
                    }}
                    onClick={() => setIsPreviewOpen(false)}
                >
                    <button
                        className="btn border-0 position-absolute"
                        style={{
                            top: "20px",
                            right: "20px",
                            color: "#000000",
                            fontSize: "2.5rem",
                            zIndex: 10000,
                            opacity: "0.7"
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsPreviewOpen(false);
                        }}
                        onMouseOver={(e) => e.currentTarget.style.opacity = "1"}
                        onMouseOut={(e) => e.currentTarget.style.opacity = "0.7"}
                    >
                        <i className="fa fa-times"></i>
                    </button>
                    <div
                        className="p-3 d-flex align-items-center justify-content-center"
                        style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            animation: "zoomIn 0.3s ease-out"
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={mainImageUrl}
                            alt="preview"
                            className="img-fluid"
                            style={{
                                maxHeight: "95vh",
                                maxWidth: "95vw",
                                objectFit: "contain",
                                boxShadow: "0 10px 30px rgba(0,0,0,0.1)"
                            }}
                        />
                    </div>
                    {/* Navigation Buttons */}
                    {imageItems.length > 1 && (
                        <>
                            <button
                                className="btn border-0 position-absolute start-0"
                                style={{
                                    paddingLeft: "30px",
                                    color: "#000",
                                    fontSize: "3rem",
                                    zIndex: 10001,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    opacity: "0.5",
                                    transition: "opacity 0.2s"
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const currentIndex = imageItems.findIndex(img => img.url === mainImageUrl);
                                    const newIndex = (currentIndex - 1 + imageItems.length) % imageItems.length;
                                    setCurrentImage(imageItems[newIndex].url);
                                }}
                                onMouseOver={(e) => e.currentTarget.style.opacity = "1"}
                                onMouseOut={(e) => e.currentTarget.style.opacity = "0.5"}
                            >
                                <i className="fa fa-chevron-left"></i>
                            </button>
                            <button
                                className="btn border-0 position-absolute end-0"
                                style={{
                                    paddingRight: "30px",
                                    color: "#000",
                                    fontSize: "3rem",
                                    zIndex: 10001,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    opacity: "0.5",
                                    transition: "opacity 0.2s"
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const currentIndex = imageItems.findIndex(img => img.url === mainImageUrl);
                                    const newIndex = (currentIndex + 1) % imageItems.length;
                                    setCurrentImage(imageItems[newIndex].url);
                                }}
                                onMouseOver={(e) => e.currentTarget.style.opacity = "1"}
                                onMouseOut={(e) => e.currentTarget.style.opacity = "0.5"}
                            >
                                <i className="fa fa-chevron-right"></i>
                            </button>
                        </>
                    )}
                    <style>
                        {`
                            @keyframes fadeIn {
                                from { opacity: 0; }
                                to { opacity: 1; }
                            }
                            @keyframes zoomIn {
                                from { transform: scale(0.9); opacity: 0; }
                                to { transform: scale(1); opacity: 1; }
                            }
                        `}
                    </style>
                </div>
            )}
        </>
    );
};

export default KidsDetailPage;
